import React from "react";
import { Card } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Lightbulb, Sun, FileText, Truck, Landmark, Map, Info, UserCheck, HardHat, Building2, Zap } from "lucide-react";

const InvestmentAssetsTab: React.FC = () => {
    return (
        <div className="px-4 space-y-8 mt-6 pb-24">
            {/* Header section */}
            <div className="pt-2 pb-2">
                <h3 className="text-2xl font-bold leading-tight">Assets needed to get project going</h3>
                <p className="text-muted-foreground text-sm font-normal leading-relaxed mt-2">
                    A detailed breakdown of required assets and infrastructure to be funded by this investment, showing exactly where your capital will be deployed.
                </p>
            </div>

            {/* Asset Cards */}
            <div className="space-y-4">
                {/* Asset 1: Solar Panels */}
                <Card className="flex flex-col gap-4 p-4 shadow-sm border border-border">
                    <div className="flex items-start justify-between">
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                                <Badge className="bg-primary/10 text-primary border-none text-[10px] hover:bg-primary/20">Capex</Badge>
                                <p className="text-muted-foreground text-xs font-medium">Core Infrastructure</p>
                            </div>
                            <p className="text-lg font-bold leading-tight">Bifacial Solar Array</p>
                            <p className="text-muted-foreground text-sm">Deployment of 50,000 high-efficiency panels</p>
                        </div>
                        <div className="w-24 h-24 bg-muted rounded-lg shrink-0 flex items-center justify-center">
                            <Sun className="w-12 h-12 text-orange-500 opacity-50" />
                        </div>
                    </div>
                </Card>

                {/* Asset 2: Storage Systems */}
                <Card className="flex flex-col gap-4 p-4 shadow-sm border border-border">
                    <div className="flex items-start justify-between">
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                                <Badge className="bg-primary/10 text-primary border-none text-[10px] hover:bg-primary/20">Capex</Badge>
                                <p className="text-muted-foreground text-xs font-medium">Energy Persistence</p>
                            </div>
                            <p className="text-lg font-bold leading-tight">Lithium-ion Battery BESS</p>
                            <p className="text-muted-foreground text-sm">20MWh utility-scale storage system</p>
                        </div>
                        <div className="w-24 h-24 bg-muted rounded-lg shrink-0 flex items-center justify-center">
                            <Zap className="w-12 h-12 text-yellow-500 opacity-50" />
                        </div>
                    </div>
                </Card>

                {/* Asset 3: Construction & Permits */}
                <Card className="flex flex-col gap-4 p-4 shadow-sm border border-border">
                    <div className="flex items-start justify-between">
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                                <Badge className="bg-primary/10 text-primary border-none text-[10px] hover:bg-primary/20">Ops</Badge>
                                <p className="text-muted-foreground text-xs font-medium">Deployment Cost</p>
                            </div>
                            <p className="text-lg font-bold leading-tight">Site Prep & Construction</p>
                            <p className="text-muted-foreground text-sm">Land clearing, mounting systems, and labor</p>
                        </div>
                        <div className="w-24 h-24 bg-muted rounded-lg shrink-0 flex items-center justify-center">
                            <HardHat className="w-12 h-12 text-blue-500 opacity-50" />
                        </div>
                    </div>
                </Card>
            </div>

            {/* Support Period Section */}
            <div className="py-2">
                <div className="bg-primary/5 dark:bg-primary/10 border border-primary/20 rounded-xl p-5 flex items-center justify-between">
                    <div>
                        <h4 className="text-primary text-sm font-bold uppercase tracking-widest">Management Team</h4>
                        <p className="text-xl font-bold mt-1">Full O&M Included</p>
                        <p className="text-muted-foreground text-xs mt-1 italic">Operations & Maintenance for first 12 months</p>
                    </div>
                    <div className="h-14 w-14 items-center justify-center rounded-full bg-primary text-white flex">
                        <UserCheck className="w-8 h-8" />
                    </div>
                </div>
            </div>

            {/* Transfer Protocol Notes */}
            <div className="py-6">
                <h3 className="text-lg font-bold mb-4">Investment Deployment Notes</h3>
                <div className="space-y-3">
                    {[
                        { icon: Building2, title: "Infrastructure Phase", desc: "Capital deployment starts within 15 days of funding" },
                        { icon: Landmark, title: "Asset Ownership", desc: "SPV established for fractional equity holders" },
                        { icon: Map, title: "Land Security", desc: "Long-term lease agreements already secured" }
                    ].map((note, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-card rounded-lg border border-border">
                            <note.icon className="text-primary w-5 h-5 mt-0.5" />
                            <div>
                                <p className="font-bold text-sm">{note.title}</p>
                                <p className="text-xs text-muted-foreground">{note.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default InvestmentAssetsTab;
